import { Injectable } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { Enums } from './enums/enums';
import { MemoryAndDateUtils } from './utils/utils-format';

export interface IDevice {
  $type: string;
  deviceId: string;
  deviceName: string;
  lastCheckInTime: Date;
  family: Enums.DeviceFamily;
  platform: Enums.DevicePlatform;
  model: string;
  manufacturer: string;
  phoneNumber: string;
  complianceStatus: boolean;
  compliancePolicyStatus: number;
  lastUpdateDate: Date;
  lastStatusUpdate: Date;
  memory: {
    totalMemory: number;
    availableMemory: number;
    availableStorage: number;
    totalStorage: number;
    availableExternalStorage: number;
    totalExternalStorage: number;
  };
  complianceItems: {
    complianceType: Enums.DeviceCompliance;
    complianceValue: boolean;
  }[];
  kind: string;
  osVersion: string;
  macAddress: string;
  hardwareSerialNumber: string;
  batteryStatus: number;
}

export interface IDeviceContract {
  _obj: IDevice;
  _active: boolean;
  _focused: boolean;
  type: string;
  family: Enums.DeviceFamily;
  form: string;
  system: string;
  telno: string;
  complianceStatusLocalized: string;
  complianceStatus: string;
  isCompliancePolicyApplicable: boolean;
  showCompliancePolicyMessage: boolean;
  compliancePolicyMessage: string;
  usedMemory: string;
  usedMemoryPercentage: number;
  usedInternalStorage: string;
  usedInternalStoragePercentage: number;
  usedExternalStorage: string;
  usedExternalStoragePercentage: number;
  availableMemory: string;
  totalMemory: string;
  totalInternalStorage: string;
  availableInternalStorage: string;
  totalExternalStorage: string;
  availableExternalStorage: string;
  platform: string;
  daysSinceLastCheckIn: string;
  complianceReasons: string[];
  memoryLabel: boolean;
  memoryStatus: string;
  internalStorageLabel: boolean;
  internalStorageStatus: string;
  externalStorageLabel: boolean;
  externalStorageStatus: string;
  deviceName: string;
  deviceId: string;
  manufacturer: string;
  model: string;
  batteryStatus: number;
  serialNumber: string;
  macAddress: string;
  osVersion: string;
}

@Injectable()
export class DeviceFormatterService {
  private logPrefix = 'DeviceFormatter: ';

  constructor(private $filter: XsI18nPipe) {}

  public contractToUI = (contract: IDevice): IDeviceContract => {
    //
    // Form Factor
    //
    let form = 'Phone';

    const contractFamilyNumber = Enums.DeviceFamily[contract.family] as unknown as Enums.DeviceFamily;
    const contractPlatformNumber = Enums.DevicePlatform[contract.platform] as unknown as Enums.DevicePlatform;

    if (
      contractFamilyNumber === Enums.DeviceFamily.WindowsDesktop ||
      contractPlatformNumber === Enums.DevicePlatform.WindowsDesktop10
    ) {
      form = 'Desktop';
    } else if (contractFamilyNumber === Enums.DeviceFamily['iOS'] && contract.model.startsWith('Mac')) {
      form = 'Desktop';
    } else if (contractFamilyNumber === Enums.DeviceFamily.iOS && contract.model.startsWith('iPad')) {
      form = 'Tablet';
    }

    //
    // Memory
    //
    let totalExternalStorage = this.$filter.transform('label_NotApplicable');
    let totalMemory = this.$filter.transform('label_NotApplicable');
    let totalInternalStorage = this.$filter.transform('label_NotApplicable');
    let memoryStatus = 'not-applicable';
    let internalStorageStatus = 'not-applicable';
    let externalStorageStatus = 'not-applicable';
    let memoryLabel = false;
    let internalStorageLabel = false;
    let externalStorageLabel = false;
    let usedMemory = '0';
    let usedExternalStorage = '0';
    let usedInternalStorage = '0';
    let usedMemoryPercentage = 0;
    let usedInternalStoragePercentage = 0;
    let usedExternalStoragePercentage = 0;
    let availableMemory = '0';
    let availableExternalStorage = '0';
    let availableStorage = '0';

    if (typeof contract.memory === 'object') {
      usedMemory = MemoryAndDateUtils.memorySize(contract.memory.totalMemory - contract.memory.availableMemory, 2);
      usedInternalStorage = MemoryAndDateUtils.memorySize(
        contract.memory.totalStorage - contract.memory.availableStorage,
        2
      );
      usedExternalStorage = MemoryAndDateUtils.memorySize(
        contract.memory.totalExternalStorage - contract.memory.availableExternalStorage,
        2
      );
      usedMemoryPercentage =
        ((contract.memory.totalMemory - contract.memory.availableMemory) / contract.memory.totalMemory) * 100;
      usedInternalStoragePercentage =
        ((contract.memory.totalStorage - contract.memory.availableStorage) / contract.memory.totalStorage) * 100;
      usedExternalStoragePercentage =
        ((contract.memory.totalExternalStorage - contract.memory.availableExternalStorage) /
          contract.memory.totalExternalStorage) *
        100;
      availableMemory = MemoryAndDateUtils.memorySize(contract.memory.availableMemory, 2);
      totalMemory = MemoryAndDateUtils.memorySize(contract.memory.totalMemory, 2);
      totalInternalStorage = MemoryAndDateUtils.memorySize(contract.memory.totalStorage, 2);
      totalExternalStorage = MemoryAndDateUtils.memorySize(contract.memory.totalExternalStorage, 2);
      availableStorage = MemoryAndDateUtils.memorySize(contract.memory.availableStorage, 2);
      availableExternalStorage = MemoryAndDateUtils.memorySize(contract.memory.availableExternalStorage, 2);
      if (totalMemory !== '0 B') {
        memoryLabel = true;
        memoryStatus = this.$filter.transform('label_Applicable');
      } else {
        totalMemory = this.$filter.transform('label_NotApplicable');
      }

      if (totalExternalStorage !== '0 B') {
        externalStorageLabel = true;
        externalStorageStatus = this.$filter.transform('label_Applicable');
      } else {
        totalExternalStorage = this.$filter.transform('label_NotApplicable');
      }

      if (totalInternalStorage !== '0 B') {
        internalStorageLabel = true;
        internalStorageStatus = this.$filter.transform('label_Applicable');
      } else {
        totalInternalStorage = this.$filter.transform('label_NotApplicable');
      }
    }

    //
    // Compliance
    //

    const isCompliancePolicyApplicable =
      contractPlatformNumber === Enums.DevicePlatform.AndroidPlus ||
      contractPlatformNumber === Enums.DevicePlatform.iOS ||
      contractPlatformNumber === Enums.DevicePlatform.Linux;
    const complianceReasons: string[] = [];

    if (contract.complianceItems && !isCompliancePolicyApplicable) {
      const reasonMessages: string[] = [];
      reasonMessages[Enums.DeviceCompliance.JailbrokenOrRooted] =
        contractFamilyNumber === Enums.DeviceFamily.iOS ? 'message_Jailbroken' : 'message_Rooted';
      reasonMessages[Enums.DeviceCompliance.ManagementDisabledByUser] = 'message_ManagementDisabledByUser';
      reasonMessages[Enums.DeviceCompliance.Wiped] = 'message_Wiped';
      reasonMessages[Enums.DeviceCompliance.DisabledByAdmin] = 'message_DisabledByAdmin';
      reasonMessages[Enums.DeviceCompliance.UnEnrolled] = 'message_UnEnrolled';
      reasonMessages[Enums.DeviceCompliance.IsEnrolled] = 'message_IsEnrolled';

      contract.complianceItems.forEach(item => {
        if (item.complianceValue === false) {
          const reasonMessage = reasonMessages[item.complianceType];
          if (!reasonMessage) {
            throw new Error('Unknown compliance type: ' + item.complianceType);
          }
          complianceReasons.push(this.$filter.transform(reasonMessage));
        }
      });
    }

    let localizedComplianceStatus = '';
    let deviceComplianceStatus = '';
    let showCompliancePolicyMessage = false;
    const contractCompliancePolicyStatus = Enums.CompliancePolicyStatus[
      contract.compliancePolicyStatus
    ] as unknown as Enums.CompliancePolicyStatus;

    if (isCompliancePolicyApplicable) {
      switch (contractCompliancePolicyStatus) {
        case Enums.CompliancePolicyStatus.Pending:
          localizedComplianceStatus = this.$filter.transform('message_Pending');
          deviceComplianceStatus = 'Pending';
          break;
        case Enums.CompliancePolicyStatus.NonCompliant:
          localizedComplianceStatus = this.$filter.transform('message_NonCompliant');
          deviceComplianceStatus = 'NonCompliant';
          break;
        case Enums.CompliancePolicyStatus.Compliant:
          localizedComplianceStatus = this.$filter.transform('message_Compliant');
          deviceComplianceStatus = 'Compliant';
          break;
        default:
          localizedComplianceStatus = this.$filter.transform('message_Unknown');
      }

      showCompliancePolicyMessage = contractCompliancePolicyStatus == Enums.CompliancePolicyStatus.NonCompliant;
    } else {
      if (contract.complianceStatus) {
        localizedComplianceStatus = this.$filter.transform('message_Compliant');
        deviceComplianceStatus = 'Compliant';
      } else {
        localizedComplianceStatus = this.$filter.transform('message_NonCompliant');
        deviceComplianceStatus = 'NonCompliant';
      }
    }

    //
    // Last Check-In
    //

    let lastCheckIn =
      contract.lastCheckInTime != null
        ? MemoryAndDateUtils.timeAgo(<any>new Date(contract.lastCheckInTime), this.$filter)
        : '';
    if (contract.lastStatusUpdate) {
      lastCheckIn = MemoryAndDateUtils.timeAgo(<any>new Date(contract.lastCheckInTime), this.$filter);
    }

    // Special case when enrolling a Win 10 Desktop to a Windows Modern device rule
    const platform =
      contractPlatformNumber === Enums.DevicePlatform.WindowsDesktop10
        ? Enums.DeviceFamily.WindowsDesktop
        : contractFamilyNumber;

    let platformName: string;
    if (platform === undefined) {
      platformName = 'Unknown';
    } else if (contractPlatformNumber === Enums.DevicePlatform.macOS) {
      platformName = Enums.DevicePlatform[contractPlatformNumber];
    } else {
      platformName = Enums.DeviceFamily[platform];
    }

    const result: IDeviceContract = {
      _obj: contract,
      _active: false,
      _focused: false,
      type: 'Device' + contract.kind,
      family: contract.family,
      form: form,
      system: contract.manufacturer + ' ' + contract.model,
      telno: contract.phoneNumber,
      complianceStatusLocalized: localizedComplianceStatus,
      complianceStatus: deviceComplianceStatus,
      isCompliancePolicyApplicable: isCompliancePolicyApplicable,
      showCompliancePolicyMessage: showCompliancePolicyMessage,
      compliancePolicyMessage: this.$filter.transform('message_CompliancePolicyNotCompliant'),
      usedMemory: usedMemory,
      usedMemoryPercentage: usedMemoryPercentage,
      usedInternalStorage: usedInternalStorage,
      usedInternalStoragePercentage: usedInternalStoragePercentage,
      usedExternalStorage: usedExternalStorage,
      usedExternalStoragePercentage: usedExternalStoragePercentage,
      availableMemory: availableMemory,
      totalMemory: totalMemory,
      totalInternalStorage: totalInternalStorage,
      availableInternalStorage: availableStorage,
      totalExternalStorage: totalExternalStorage,
      availableExternalStorage: availableExternalStorage,
      platform: platformName,
      daysSinceLastCheckIn: lastCheckIn,
      complianceReasons: complianceReasons,
      memoryLabel: memoryLabel,
      memoryStatus: memoryStatus,
      internalStorageLabel: internalStorageLabel,
      internalStorageStatus: internalStorageStatus,
      externalStorageLabel: externalStorageLabel,
      externalStorageStatus: externalStorageStatus,
      deviceName: contract.deviceName,
      deviceId: contract.deviceId,
      manufacturer: contract.manufacturer,
      model: contract.model,
      batteryStatus: contract.batteryStatus,
      serialNumber: contract.hardwareSerialNumber,
      macAddress: contract.macAddress,
      osVersion: contract.osVersion,
    };

    return result;
  };

  public contractsToUI = (contracts: IDevice[]): IDeviceContract[] => {
    if (!Array.isArray(contracts)) {
      throw new Error('Invalid parameter: contracts');
    }

    const result: IDeviceContract[] = [];
    contracts.forEach(c => {
      result.push(this.contractToUI(c));
    });

    return result;
  };
}
